import {
  Mic,
  Square,
  Trash2,
  ExternalLink,
  Clipboard,
  Settings2,
  ToggleLeft,
  ToggleRight,
  ArrowUp,
  ArrowDown,
  AlignVerticalJustifyCenter,
  Sun,
  Check,
  Moon,
  FileEdit,
  GraduationCap,
  RotateCw,
  Activity,
  Clock,
  Menu as MenuIcon,
} from "lucide-react";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { SavedTranscription, ROIEventType } from "../types";
import { AudioRecorder, blobToBase64 } from "../services/audioUtils";
import { transcribeAudio, postProcessReport } from "../services/geminiService";
import { copyToClipboard } from "../services/clipboardUtils";
import { recordROIEvent, isMobileDevice, streamTextToCloud } from "../services/db";
import { auth } from "../firebaseConfig";
import { sanitizeHtml } from "../utils/sanitize";
import {
  saveTranscriptionBackup,
  deleteTranscriptionBackup,
} from "../services/transcriptionBackupService";
import RetryTranscriptionButton from "./RetryTranscriptionButton";
import Modal from "./Modal";
import AIAssistantMenu from "./AIAssistantMenu";

// Identificador único para esta pestaña/ventana para evitar eco en BroadcastChannel
if (typeof window !== "undefined" && !(window as any).__MODULAR_TAB_ID__) {
  (window as any).__MODULAR_TAB_ID__ = Math.random()
    .toString(36)
    .substring(2, 15);
}
const TAB_ID = (window as any).__MODULAR_TAB_ID__;

interface DictaphoneScreenProps {
  savedTranscriptions: SavedTranscription[];
  onSaveTranscription: (item: SavedTranscription) => void;
  onDeleteTranscription: (id: string) => void;
  onClearAll: () => void;
  onSendToEditor: (text: string) => void;
  onToggleMini: () => void;
  onSavePhrase?: (text: string) => void;
}

const DictaphoneScreen: React.FC<DictaphoneScreenProps> = ({
  savedTranscriptions,
  onSaveTranscription,
  onDeleteTranscription,
  onClearAll,
  onSendToEditor,
}) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("module_theme_dictaphone");
    if (saved) return saved === "dark";
    return localStorage.getItem("app_theme") === "dark";
  });

  const [autoCopy, setAutoCopy] = useState(true);
  const [autoStopEnabled, setAutoStopEnabled] = useState(
    () => localStorage.getItem("dictaphone_autoStop") === "true",
  );
  const [silenceDuration, setSilenceDuration] = useState(() =>
    parseInt(localStorage.getItem("dictaphone_silence") || "5"),
  );
  const [engineMode, setEngineMode] = useState<"phd" | "ia">(() => {
    const saved = localStorage.getItem("dictaphone_engine_mode");
    return saved === "phd" || saved === "ia" ? (saved as any) : "ia";
  });

  const isRadiologyMaster =
    auth.currentUser?.email?.toLowerCase() === "dr.francisco.reyes@gmail.com";
  const isDevMode = localStorage.getItem("developer_mode") === "true";

  useEffect(() => {
    localStorage.setItem("dictaphone_engine_mode", engineMode);
    engineModeRef.current = engineMode;
  }, [engineMode]);

  const lastLiveTextRef = useRef("");

  const [micPosition, setMicPosition] = useState<"top" | "center" | "bottom">(
    () => {
      const saved = localStorage.getItem("dictaphone_mic_pos");
      return saved === "top" || saved === "center" || saved === "bottom"
        ? saved
        : "bottom";
    },
  );

  const [panelSizes, setPanelSizes] = useState<[number, number]>(() => {
    const saved = localStorage.getItem("dictaphone_panel_proportions");
    return saved ? JSON.parse(saved) : [20, 40];
  });

  const [hasSelectedKey, setHasSelectedKey] = useState(true);

  useEffect(() => {
    const checkKey = async () => {
      if (window.aistudio) {
        const selected = await window.aistudio.hasSelectedApiKey();
        setHasSelectedKey(selected);
      }
    };
    checkKey();
  }, []);

  const handleSelectKey = async () => {
    if (window.aistudio) {
      await window.aistudio.openSelectKey();
      setHasSelectedKey(true);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const autoCopyRef = useRef(autoCopy);
  const autoStopRef = useRef(autoStopEnabled);
  const silenceDurationRef = useRef(silenceDuration);
  const engineModeRef = useRef(engineMode);

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activeSkillsMenuId, setActiveSkillsMenuId] = useState<string | null>(
    null,
  );
  const [skillProcessingId, setSkillProcessingId] = useState<string | null>(
    null,
  );
  const [sentIdsSession, setSentIdsSession] = useState<Set<string>>(new Set());
  const isRecordingRef = useRef(false);
  const isTogglingRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRemoteLocked, setIsRemoteLocked] = useState(false);
  const [liveStreamText, setLiveStreamText] = useState("");

  // Sincronización entre instancias para evitar doble envío
  useEffect(() => {
    const bc = new BroadcastChannel("modular_sync_channel");
    bc.onmessage = (event) => {
      if (
        event.data.type === "RECORDING_START" &&
        event.data.senderId !== TAB_ID
      ) {
        setIsRemoteLocked(true);
      } else if (
        event.data.type === "RECORDING_STOP" &&
        event.data.senderId !== TAB_ID
      ) {
        setIsRemoteLocked(false);
      }
    };

    // Al cargar, verificar si hay grabaciones activas (opcional, pero Broadcast es mejor para tiempo real)
    return () => bc.close();
  }, []);

  const recorderRef = useRef<AudioRecorder | null>(null);
  const liveManagerRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const visualizerStreamRef = useRef<MediaStream | null>(null);
  const silenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const silenceStartRef = useRef<number | null>(null);
  const isStoppingRef = useRef(false);
  const autoThresholdRef = useRef<number>(65); // Umbral base
  const calibrationFramesRef = useRef<number>(0);
  const noiseSumRef = useRef<number>(0);
  const recordStartTimeRef = useRef<number>(0);

  useEffect(() => {
    const handleThemeChange = () => {
      const saved = localStorage.getItem("module_theme_dictaphone");
      if (!saved) setIsDarkMode(localStorage.getItem("app_theme") === "dark");
    };
    window.addEventListener("app_theme_changed", handleThemeChange);
    return () =>
      window.removeEventListener("app_theme_changed", handleThemeChange);
  }, []);

  useEffect(() => {
    localStorage.setItem("dictaphone_engine_mode", engineMode);
    engineModeRef.current = engineMode;
  }, [engineMode]);

  const toggleDarkMode = useCallback(() => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem("module_theme_dictaphone", next ? "dark" : "day");
    window.dispatchEvent(new Event('module_theme_changed'));
  }, [isDarkMode]);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(
        "dictaphone_panel_proportions",
        JSON.stringify(panelSizes),
      );
    }, 500);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [panelSizes]);

  const performCopy = useCallback(async (text: string, itemId?: string) => {
    // Limpiar etiquetas HTML para un pegado limpio en PACS
    const cleanedText = text
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]*>?/gm, "")
      .replace(/&nbsp;/g, " ")
      .trim();

    const success = await copyToClipboard(cleanedText);
    if (success && itemId) {
      setCopiedId(itemId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, []);

  const handleResizeStart = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const startY = e.clientY;
      const totalHeight = container.clientHeight;
      const startSizes = [...panelSizes];
      document.body.style.cursor = "row-resize";
      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaPercent = ((moveEvent.clientY - startY) / totalHeight) * 100;
        const newSizes = [...startSizes];
        newSizes[index] = Math.max(
          10,
          Math.min(80, startSizes[index] + deltaPercent),
        );
        setPanelSizes([newSizes[0], newSizes[1]]);
      };
      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [panelSizes],
  );

  const stopAudioProcessing = useCallback(() => {
    if (silenceIntervalRef.current) clearInterval(silenceIntervalRef.current);
    silenceIntervalRef.current = null;
    if (sourceRef.current) sourceRef.current.disconnect();
    if (visualizerStreamRef.current) {
      visualizerStreamRef.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      visualizerStreamRef.current = null;
    }
  }, []);

  const processTranscriptionWithRetry = async (
    audioSource: string | Blob,
    mimeType: string,
    engine: "phd" | "ia",
    retries = 0,
  ): Promise<string> => {
    try {
      const text = await transcribeAudio(audioSource, mimeType, engine);

      if (!text || text.trim().length < 2) {
        throw new Error(
          "Falló el procesamiento remoto (Resultado vacío o silencio)",
        );
      }
      return text;
    } catch (error) {
      if (retries > 0) {
        console.warn(
          `Error en transcripción. Reintentando automáticamente (${retries} restantes)...`,
        );
        // Pequeño delay para el reintento automático
        await new Promise((resolve) => setTimeout(resolve, 800));
        return processTranscriptionWithRetry(
          audioSource,
          mimeType,
          engine,
          retries - 1,
        );
      }
      throw error;
    }
  };

  const stopRecording = useCallback(async () => {
    if (isTogglingRef.current || !isRecordingRef.current || isProcessing)
      return;
    isTogglingRef.current = true;

    const duration = Date.now() - recordStartTimeRef.current;

    // Notificar liberación de instancia
    const bc = new BroadcastChannel("modular_sync_channel");
    bc.postMessage({ type: "RECORDING_STOP", senderId: TAB_ID });
    bc.close();

    if (duration < 400) {
      setIsRecording(false);
      isRecordingRef.current = false;
      stopAudioProcessing();
      if (recorderRef.current) await recorderRef.current.stop().catch(() => {});
      if (liveManagerRef.current)
        await liveManagerRef.current.stop().catch(() => {});
      setLiveStreamText("");
      isTogglingRef.current = false;
      return;
    }

    setIsProcessing(true);
    setIsRecording(false);
    isRecordingRef.current = false;
    stopAudioProcessing();

    try {
      if (recorderRef.current) {
        const { blob, mimeType } = await recorderRef.current.stop();
        if (blob.size >= 500) {
          const base64 = await blobToBase64(blob);

          // PERSISTENCIA PRE-ENVÍO
          const backupId = `dict_${Date.now()}`;
          await saveTranscriptionBackup({
            id: backupId,
            blobBase64: base64,
            mimeType,
            timestamp: Date.now(),
            module: "dictaphone",
            engine: engineMode,
            durationSeconds: Math.round((Date.now() - recordStartTimeRef.current) / 1000),
          });

          try {
            const text = await processTranscriptionWithRetry(
              blob,
              mimeType,
              engineMode,
            );

            if (text && text.trim().length >= 2) {
              if (isMobileDevice() && auth.currentUser) {
                streamTextToCloud(auth.currentUser.uid, text, 'final');
              }
              const newId = Date.now().toString();
              onSaveTranscription({
                id: newId,
                text,
                mode: "ia",
                status: "success",
                createdAt: Date.now(),
              });
              if (auth.currentUser)
                await recordROIEvent(
                  auth.currentUser.uid,
                  ROIEventType.TRANSCRIPTION,
                );
              if (autoCopyRef.current) await performCopy(text, newId);
            }

            // ÉXITO: Borrar backup
            await deleteTranscriptionBackup(backupId);
          } catch (error) {
            console.error(
              "Transcription execution error in Dictaphone after retries:",
              error,
            );

            // Marcar el backup como error para que aparezca el botón de reintento inmediatamente
            await saveTranscriptionBackup({
              id: backupId,
              blobBase64: base64,
              mimeType,
              timestamp: Date.now(),
              module: "dictaphone",
              engine: engineMode,
              isError: true,
              durationSeconds: Math.round((Date.now() - recordStartTimeRef.current) / 1000),
            });

            // INSERTAR MARCADOR DE FALLO EN LA LISTA
            const errorId = `failed_${Date.now()}`;
            onSaveTranscription({
              id: errorId,
              text: "Error de procesamiento (Silencio/Fallo detectado)",
              mode: "ia",
              status: "error",
              errorDetails:
                error instanceof Error ? error.message : String(error),
              backupId: backupId,
              createdAt: Date.now(),
            });
          }
        }
      }
    } catch (e: any) {
      console.error("Stop Recording error:", e);
    } finally {
      setIsProcessing(false);
      setLiveStreamText("");
      isStoppingRef.current = false;
      isTogglingRef.current = false;
    }
  }, [
    engineMode,
    isProcessing,
    onSaveTranscription,
    performCopy,
    stopAudioProcessing,
  ]);

  const startAudioProcessing = useCallback(
    (stream: MediaStream) => {
      visualizerStreamRef.current = stream;
      if (!audioContextRef.current)
        audioContextRef.current = new (
          window.AudioContext || (window as any).webkitAudioContext
        )({ sampleRate: 16000 });
      if (audioContextRef.current.state === "suspended")
        audioContextRef.current.resume();

      // OPTIMIZACIÓN: Reducir smoothing para detección de silencio instantánea
      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.3;
      }

      try {
        if (sourceRef.current) sourceRef.current.disconnect();
        sourceRef.current =
          audioContextRef.current.createMediaStreamSource(stream);
        sourceRef.current.connect(analyserRef.current);
      } catch (e) {
        console.error("Error connecting audio source:", e);
        return;
      }

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      silenceStartRef.current = null;
      isStoppingRef.current = false;

      // Iniciar calibración de ruido base
      calibrationFramesRef.current = 10; // Medir durante 1 segundo aprox (10 * 100ms)
      noiseSumRef.current = 0;

      if (silenceIntervalRef.current) clearInterval(silenceIntervalRef.current);
      silenceIntervalRef.current = setInterval(() => {
        if (!analyserRef.current || isStoppingRef.current) return;

        // LÍMITE TÉCNICO: 5 MINUTOS (300 SEGUNDOS)
        const currentDuration =
          (Date.now() - recordStartTimeRef.current) / 1000;
        if (currentDuration > 300) {
          console.warn(
            "Límite de tiempo alcanzado (5 min). Deteniendo grabación.",
          );
          isStoppingRef.current = true;
          stopRecording();
          return;
        }

        // RE-SINCRONIZACIÓN FORZADA: Leer directamente de las refs en cada ciclo
        const isAutoStopActive = autoStopRef.current;
        const currentSilenceLimit = silenceDurationRef.current;

        analyserRef.current.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) sum += dataArray[i];
        const avgNoise = sum / bufferLength;

        // Calibración dinámica del umbral (primeros frames)
        if (calibrationFramesRef.current > 0) {
          noiseSumRef.current += avgNoise;
          calibrationFramesRef.current--;
          if (calibrationFramesRef.current === 0) {
            const baseNoise = noiseSumRef.current / 10;
            autoThresholdRef.current = Math.max(55, baseNoise + 15); // Umbral inteligente
            console.log(
              `Auto-pausa: Calibración completada. Ruido base: ${Math.round(baseNoise)}, Umbral fijado en: ${Math.round(autoThresholdRef.current)}`,
            );
          }
          return; // Esperar a que termine la calibración
        }

        // Actualizar visualizador
        // Detección de silencio
        if (isAutoStopActive) {
          if (avgNoise < autoThresholdRef.current) {
            if (silenceStartRef.current === null) {
              silenceStartRef.current = Date.now();
              console.log(
                `Auto-pausa: Silencio detectado (Nivel: ${Math.round(avgNoise)} < ${Math.round(autoThresholdRef.current)}). Esperando ${currentSilenceLimit}s...`,
              );
            } else {
              const silenceSecs = (Date.now() - silenceStartRef.current) / 1000;
              if (silenceSecs >= currentSilenceLimit) {
                console.log(
                  `Auto-pausa: EJECUTANDO PARADA tras ${silenceSecs.toFixed(1)}s de silencio.`,
                );
                isStoppingRef.current = true;
                stopRecording();
              }
            }
          } else {
            if (silenceStartRef.current !== null) {
              console.log(
                `Auto-pausa: Voz detectada (Nivel: ${Math.round(avgNoise)}). Reiniciando contador.`,
              );
            }
            silenceStartRef.current = null;
          }
        } else {
          silenceStartRef.current = null;
        }
      }, 100);
    },
    [stopRecording],
  );

  const startRecording = useCallback(async () => {
    if (
      isTogglingRef.current ||
      isProcessing ||
      isRecordingRef.current ||
      isRemoteLocked
    )
      return;

    isTogglingRef.current = true;

    // Notificar bloqueo de instancia a otras ventanas
    const bc = new BroadcastChannel("modular_sync_channel");
    bc.postMessage({ type: "RECORDING_START", senderId: TAB_ID });
    bc.close();

    setLiveStreamText("");
    lastLiveTextRef.current = "";
    recordStartTimeRef.current = Date.now();

    isRecordingRef.current = true;
    setIsRecording(true);

    try {
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.start();
      const stream = recorderRef.current.getStream();
      if (stream) startAudioProcessing(stream);
    } catch {
      setIsRecording(false);
      isRecordingRef.current = false;
      alert("No se ha detectado entrada de audio. Verifique su micrófono.");
    } finally {
      isTogglingRef.current = false;
    }
  }, [isProcessing, startAudioProcessing]);

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const spaceEnabled =
        localStorage.getItem("app_shortcut_space_record") !== "false";
      if (!spaceEnabled) return;
      if (
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLInputElement
      )
        return;
      if (e.code === "Space") {
        e.preventDefault();
        if (isRecording) stopRecording();
        else if (!isProcessing && !isRemoteLocked) startRecording();
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isRecording, isProcessing, startRecording, stopRecording]);

  useEffect(() => {
    autoCopyRef.current = autoCopy;
  }, [autoCopy]);
  useEffect(() => {
    autoStopRef.current = autoStopEnabled;
  }, [autoStopEnabled]);
  useEffect(() => {
    silenceDurationRef.current = silenceDuration;
  }, [silenceDuration]);
  useEffect(() => {
    engineModeRef.current = engineMode;
  }, [engineMode]);

  useEffect(() => {
    const handleSettingsUpdate = () => {
      const savedAutoCopy = localStorage.getItem("dictaphone_autoCopy");
      const savedAutoStop = localStorage.getItem("dictaphone_autoStop");
      const savedSilence = localStorage.getItem("dictaphone_silence");
      const savedMode = localStorage.getItem("dictaphone_engine_mode");

      if (savedAutoCopy !== null) {
        const val = savedAutoCopy === "true";
        setAutoCopy(val);
        autoCopyRef.current = val;
      }
      if (savedAutoStop !== null) {
        const val = savedAutoStop === "true";
        setAutoStopEnabled(val);
        autoStopRef.current = val;
      }
      if (savedSilence !== null) {
        const val = Math.max(1, Math.min(4, parseInt(savedSilence)));
        setSilenceDuration(val);
        silenceDurationRef.current = val;
      }
      if (savedMode !== null) {
        setEngineMode(savedMode as any);
        engineModeRef.current = savedMode as any;
      }
    };

    // Carga inicial
    handleSettingsUpdate();

    // Escuchar actualizaciones externas (ej. desde el menú de ajustes o Redacción PhD)
    window.addEventListener("dictaphone_settings_update", handleSettingsUpdate);
    return () =>
      window.removeEventListener(
        "dictaphone_settings_update",
        handleSettingsUpdate,
      );
  }, []);

  useEffect(() => {
    localStorage.setItem("dictaphone_autoCopy", String(autoCopy));
    localStorage.setItem("dictaphone_autoStop", String(autoStopEnabled));
    localStorage.setItem("dictaphone_silence", String(silenceDuration));
    localStorage.setItem("dictaphone_mic_pos", micPosition);
    localStorage.setItem("dictaphone_engine_mode", engineMode);

    // Sincronizar refs inmediatamente para el intervalo de audio
    autoStopRef.current = autoStopEnabled;
    silenceDurationRef.current = silenceDuration;

    window.dispatchEvent(new Event("dictaphone_settings_update"));
  }, [autoCopy, autoStopEnabled, silenceDuration, micPosition, engineMode]);

  useEffect(() => {
    return () => {
      stopAudioProcessing();
    };
  }, [stopAudioProcessing]);

  const renderConfigSegment = () => (
    <div
      className={`h-full flex flex-col min-h-0 ${isDarkMode ? "bg-[#2a2d34]" : "bg-white"}`}
    >
      <div
        className={`px-3 py-2 border-b flex justify-between items-center shrink-0 ${isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-gray-50 border-gray-100"}`}
      >
        <div className="flex items-center space-x-1.5">
          <Settings2 size={14} className="text-gray-500" />
          <span
            className={`text-[10px] font-bold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Ajustes de Transcripción
          </span>
        </div>
        <button
          onClick={() =>
            window.open(
              `${window.location.origin}${window.location.pathname}?widget=recorder`,
              `MODULAR_MINI_${Date.now()}`,
              `width=320,height=450`,
            )
          }
          className="text-gray-400 hover:text-blue-500 transition-colors"
        >
          <ExternalLink size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
        <div className="flex flex-col gap-3 max-w-full">
          <div
            className={`p-3 rounded-xl border flex flex-col transition-all ${isDarkMode ? "bg-indigo-900/5 border-indigo-900/20" : "bg-indigo-50/30 border-indigo-100"}`}
          >
            <span className="text-[8px] font-black text-indigo-500 uppercase mb-2 tracking-widest text-center">
              Arquitectura de Procesamiento
            </span>
            <div
              className={`flex rounded-lg p-0.5 border shadow-inner ${isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-white border-indigo-50"}`}
            >
              <button
                onClick={() => setEngineMode("phd")}
                className={`flex-1 py-1.5 rounded-md text-[8px] font-black flex items-center justify-center transition-all ${engineMode === "phd" ? "bg-blue-600 text-white shadow-md" : "text-gray-400 hover:text-gray-300"}`}
              >
                <GraduationCap size={12} className="mr-1" /> PHD
              </button>
              <button
                onClick={() => setEngineMode("ia")}
                className={`flex-1 py-1.5 rounded-md text-[8px] font-black flex items-center justify-center transition-all ${engineMode === "ia" ? "bg-indigo-600 text-white shadow-md" : "text-gray-400 hover:text-gray-200"}`}
              >
                <Activity size={12} className="mr-1" /> GM1
              </button>
            </div>
          </div>

          <div
            className={`p-3 rounded-xl border flex flex-col transition-all ${isDarkMode ? "bg-blue-900/5 border-blue-900/20" : "bg-blue-50/50 border-blue-100"}`}
          >
            <span className="text-[8px] font-black text-blue-500 uppercase mb-2 tracking-widest text-center">
              Posición Interfaz
            </span>
            <div
              className={`flex rounded-lg p-0.5 border shadow-inner ${isDarkMode ? "bg-[#1e2024] border-gray-700" : "bg-white border-blue-50"}`}
            >
              {(["top", "center", "bottom"] as const).map((pos) => (
                <button
                  key={pos}
                  onClick={() => setMicPosition(pos)}
                  className={`flex-1 py-1 rounded-md text-[10px] flex items-center justify-center transition-all ${micPosition === pos ? "bg-blue-600 text-white shadow-md" : "text-gray-400 hover:bg-gray-500/10"}`}
                >
                  {pos === "top" && <ArrowUp size={12} />}
                  {pos === "center" && <AlignVerticalJustifyCenter size={12} />}
                  {pos === "bottom" && <ArrowDown size={12} />}
                </button>
              ))}
            </div>
          </div>

          <div
            className={`p-3 rounded-xl border flex items-center justify-between transition-all ${autoCopy ? (isDarkMode ? "bg-emerald-900/5 border-emerald-900/20" : "bg-emerald-50 border-emerald-200") : isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
          >
            <span
              className={`text-[9px] font-black uppercase tracking-widest ${autoCopy ? "text-emerald-500" : "text-gray-400"}`}
            >
              Auto-Copy
            </span>
            <button
              onClick={() => setAutoCopy(!autoCopy)}
              className="transition-transform duration-200 active:scale-90"
            >
              {autoCopy ? (
                <ToggleRight size={22} className="text-emerald-600" />
              ) : (
                <ToggleLeft
                  size={22}
                  className="text-gray-300 dark:text-gray-600"
                />
              )}
            </button>
          </div>

          <div
            className={`p-3 rounded-xl border flex flex-col gap-2 transition-all ${autoStopEnabled ? (isDarkMode ? "bg-amber-900/5 border-amber-900/20" : "bg-amber-50 border-amber-200") : isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
          >
            <div className="flex justify-between items-center">
              <span
                className={`text-[9px] font-black uppercase tracking-widest ${autoStopEnabled ? "text-amber-500" : "text-gray-400"}`}
              >
                Auto-Pausa
              </span>
              <button
                onClick={() => setAutoStopEnabled(!autoStopEnabled)}
                className="transition-transform duration-200 active:scale-90"
              >
                {autoStopEnabled ? (
                  <ToggleRight size={22} className="text-amber-600" />
                ) : (
                  <ToggleLeft
                    size={22}
                    className="text-gray-300 dark:text-gray-600"
                  />
                )}
              </button>
            </div>
            <div
              className={`flex items-center space-x-2 ${!autoStopEnabled && "opacity-30 pointer-events-none"}`}
            >
              <Clock size={10} className="text-amber-500 shrink-0" />
              <input
                type="range"
                min="1"
                max="15"
                step="1"
                value={silenceDuration}
                onChange={(e) => setSilenceDuration(parseInt(e.target.value))}
                className="w-full h-1 bg-gray-200 rounded-lg appearance-none accent-amber-500"
              />
              <span className="text-[9px] font-black text-amber-500 w-4">
                {silenceDuration}s
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMicSegment = () => (
    <div
      className={`h-full flex flex-col items-center justify-center relative overflow-hidden transition-colors ${isDarkMode ? "bg-[#1e2024]" : "bg-white"}`}
    >
      <div className="relative z-10 flex flex-col items-center justify-center w-full p-4">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isRecording) stopRecording();
            else if (!isProcessing) startRecording();
          }}
          disabled={isProcessing || isRemoteLocked}
          className={`relative z-10 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 active:scale-95 ${isRecording ? "w-28 h-28 bg-red-500 animate-pulse ring-8 ring-red-500/10" : isProcessing ? "w-28 h-28 bg-gray-600" : "w-28 h-28 bg-blue-600 hover:bg-blue-700 hover:scale-105 ring-8 ring-blue-500/10"}`}
        >
          {isProcessing ? (
            <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
          ) : isRecording ? (
            <Square size={38} className="text-white fill-current" />
          ) : (
            <Mic size={48} className="text-white" />
          )}
        </button>

        <div className="mt-6 min-h-[4rem] flex flex-col items-center justify-center max-w-[85%]">
          {isRecording ? (
            <div className="flex flex-col items-center space-y-3">
              <span className="text-xs font-black text-red-500 tracking-[0.2em] uppercase animate-pulse">
                Escuchando Audio...
              </span>
            </div>
          ) : isProcessing ? (
            <span
              className={`text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest ${isDarkMode ? "bg-blue-900/30 text-blue-400" : "bg-blue-50 text-blue-600"}`}
            >
              Analizando...
            </span>
          ) : (
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
              Mantén pulsado o usa Espacio
            </span>
          )}
          <div
            className={`flex items-center space-x-1.5 mt-4 transition-opacity ${isRecording || isProcessing ? "opacity-0" : "opacity-40"}`}
          >
            {engineMode === "phd" ? (
              <GraduationCap size={12} className="text-indigo-500" />
            ) : (
              <Activity size={12} className="text-indigo-500" />
            )}
            <span
              className={`text-[9px] font-black uppercase tracking-widest ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Motor {engineMode === "phd" ? "PhD Tradicional" : "GM 1"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderHistorySegment = () => (
    <div
      className={`h-full flex flex-col min-h-0 ${isDarkMode ? "bg-[#1e2024]" : "bg-gray-50"}`}
    >
      <div
        className={`px-3 py-2 border-b flex justify-between items-center shrink-0 ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="flex items-center space-x-1.5">
          <Clipboard size={14} className="text-gray-500" />
          <span
            className={`text-[10px] font-bold uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Historial de Sesión
          </span>
        </div>
        {savedTranscriptions.length > 0 && (
          <button
            onClick={onClearAll}
            className={`flex items-center space-x-1 text-[9px] font-black border px-2.5 py-1 rounded-lg transition-all ${isDarkMode ? "bg-gray-800 border-gray-700 text-gray-500 hover:text-red-400" : "bg-white border-gray-200 text-gray-400 hover:text-red-500"}`}
          >
            <Trash2 size={12} />
            <span>LIMPIAR</span>
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
        {/* TARJETA VIVA (LIVE PREVIEW) */}
        {(isRecording || isProcessing) && (
          <div
            className={`rounded-xl shadow-sm border overflow-hidden transition-all ${isDarkMode ? "bg-[#2a2d34] border-blue-500/30" : "bg-white border-blue-200"}`}
          >
            <div
              className={`px-3 py-1.5 border-b flex justify-between items-center ${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50/80 border-gray-100"}`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-[10px] font-bold text-gray-400">
                  En vivo
                </span>
                <span
                  className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter ${
                    engineMode === "phd"
                      ? isDarkMode
                        ? "bg-blue-900/40 text-blue-400"
                        : "bg-blue-100 text-blue-700"
                      : isDarkMode
                        ? "bg-indigo-900/40 text-indigo-400"
                        : "bg-indigo-100 text-indigo-700"
                  }`}
                >
                  {engineMode === "phd" ? "PHD" : "GM1"}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse ml-1"></span>
              </div>
              <div className="flex items-center space-x-1 opacity-20 text-gray-400">
                <Sun size={14} />
                <FileEdit size={14} />
                <Trash2 size={14} />
              </div>
            </div>
            <div className="p-4">
              {isRecording && !liveStreamText && (
                <div className="flex flex-col items-center justify-center py-6 space-y-3">
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce`}
                        style={{ animationDelay: `${i * 0.15}s` }}
                      ></div>
                    ))}
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                    Escuchando...
                  </p>
                  <p className="text-[9px] font-bold uppercase text-gray-500/50">
                    {null}
                  </p>
                </div>
              )}

              {isProcessing && !liveStreamText && (
                <div className="flex flex-col items-center justify-center py-6 space-y-3">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500">
                    Procesando bloque final...
                  </p>
                </div>
              )}

              {liveStreamText && (
                <p
                  className={`text-[13px] font-medium leading-relaxed whitespace-pre-wrap ${isProcessing ? "text-gray-400" : isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  {liveStreamText}
                  {isRecording && (
                    <span className="inline-block w-1 h-4 ml-1 bg-blue-500 animate-pulse align-middle"></span>
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        {savedTranscriptions.length === 0 &&
          !liveStreamText &&
          !isRecording &&
          !isProcessing && (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 opacity-20">
              <Clipboard size={32} className="mb-2" />
              <p className="text-[10px] font-black uppercase tracking-widest">
                Sin capturas
              </p>
            </div>
          )}
        {savedTranscriptions.map((item) => {
          const isSent = sentIdsSession.has(item.id);
          const isError = item.status === "error";
          return (
            <div
              key={item.id}
              className={` rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 group relative ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-gray-200"} ${isError ? "ring-2 ring-red-500/20 border-red-500/30" : ""}`}
            >
              {/* Indicador de copiado tipo biblioteca */}
              {copiedId === item.id && (
                <div className="absolute top-2 right-2 flex items-center space-x-1.5 bg-green-500 text-white px-2.5 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-lg animate-in fade-in zoom-in duration-200 z-10">
                  <Check size={10} />
                  <span>Copiado</span>
                </div>
              )}
              <div
                className={`px-3 py-1.5 border-b flex justify-between items-center transition-colors duration-300 ${isDarkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50/80 border-gray-100"}`}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-[10px] font-bold text-gray-400">
                    {new Date(item.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span
                    className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter ${isDarkMode ? "bg-indigo-900/40 text-indigo-400" : "bg-indigo-100 text-indigo-700"}`}
                  >
                    IA
                  </span>
                  {copiedId === item.id && (
                    <span className="flex items-center space-x-1 text-[8px] font-black text-purple-500 uppercase tracking-widest animate-in fade-in slide-in-from-left-2 duration-300">
                      <Check size={10} />
                      <span>Copiado</span>
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-1 opacity-40 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveSkillsMenuId(
                        activeSkillsMenuId === item.id ? null : item.id,
                      );
                    }}
                    className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${activeSkillsMenuId === item.id ? (isDarkMode ? "bg-blue-600 text-white" : "bg-blue-600 text-white") : isDarkMode ? "bg-gray-800 text-gray-400 hover:text-blue-400" : "bg-gray-100 text-gray-500 hover:text-blue-600"}`}
                  >
                    <MenuIcon size={12} />
                    <span>MENÚ</span>
                  </button>
                  <button
                    onClick={() => {
                      onSendToEditor(item.text);
                      setSentIdsSession((prev) => new Set(prev).add(item.id));
                    }}
                    className={`p-1.5 rounded transition-all ${isSent ? "text-green-500 bg-green-500/10" : "text-gray-400 hover:text-blue-500 hover:bg-blue-500/10"}`}
                    title="Enviar a MODULAR-1"
                  >
                    {isSent ? <Check size={14} /> : <Sun size={14} />}
                  </button>
                  <button
                    onClick={() => onDeleteTranscription(item.id)}
                    className="p-1.5 hover:bg-red-500/10 hover:text-red-500 rounded text-gray-400"
                    title="Borrar"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* MENÚ DE HABILIDADES EXPANDIBLE */}
              {activeSkillsMenuId === item.id && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className={`p-3 border-b flex flex-wrap items-center gap-1.5 animate-in slide-in-from-top-2 duration-200 ${isDarkMode ? "bg-gray-800/30" : "bg-blue-50/30"}`}
                >
                  <AIAssistantMenu
                    isDark={isDarkMode}
                    versionIndex={
                      item.versionIndex !== undefined
                        ? item.versionIndex
                        : item.history
                          ? item.history.length - 1
                          : 0
                    }
                    historyCount={item.history ? item.history.length : 1}
                    onNavigateHistory={(idx) => {
                      onSaveTranscription({
                        ...item,
                        text: item.history![idx],
                        versionIndex: idx,
                      });
                    }}
                    onPostProcess={(action, lang) =>
                      handleApplySkill(item, action as any, lang)
                    }
                    isProcessing={skillProcessingId === item.id}
                    showCopy={false}
                  />
                </div>
              )}

              <div
                onClick={() => !isError && performCopy(item.text, item.id)}
                className={`p-4 relative transition-colors duration-300 ${!isError ? "cursor-pointer" : ""} ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`}
              >
                <div
                  className={`text-[13px] font-medium leading-relaxed transition-colors duration-300 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  {skillProcessingId === item.id ? (
                    <div className="flex flex-col items-center justify-center py-2 space-y-2 opacity-50">
                      <RotateCw
                        size={16}
                        className="animate-spin text-blue-500"
                      />
                      <span className="text-[10px] font-black uppercase tracking-widest text-blue-500">
                        Procesando Habilidad...
                      </span>
                    </div>
                  ) : (
                    <div
                      className="whitespace-pre-wrap medical-rich-text"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(item.text
                          .replace(/<b>/g, '<strong class="font-black">')
                          .replace(/<\/b>/g, "</strong>")
                          .replace(/\n/g, "<br/>")),
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderSeparator = (onMouseDown: (e: React.MouseEvent) => void) => (
    <div
      onMouseDown={onMouseDown}
      className={`h-[1px] cursor-row-resize z-20 shrink-0 transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-blue-600" : "bg-gray-200 hover:bg-blue-400"}`}
    />
  );

  const handleApplySkill = async (
    item: SavedTranscription,
    action: "optimize" | "pro" | "translate" | "structure" | "narrative",
    targetLang?: string,
  ) => {
    if (skillProcessingId) return;
    setSkillProcessingId(item.id);
    try {
      const processedText = await postProcessReport(
        item.text,
        action,
        targetLang,
      );
      if (processedText && processedText !== item.text) {
        const currentHistory = item.history || [item.text];
        const currentIndex =
          item.versionIndex !== undefined
            ? item.versionIndex
            : currentHistory.length - 1;

        // Truncar historial si estamos en medio de las versiones y creamos una nueva rama
        const newHistory = currentHistory.slice(0, currentIndex + 1);
        newHistory.push(processedText);

        onSaveTranscription({
          ...item,
          text: processedText,
          history: newHistory,
          versionIndex: newHistory.length - 1,
        });

        // Limpieza de seguridad para evitar persistencia en nuevos dictados
        if (liveStreamText === item.text) {
          setLiveStreamText("");
        }

        // Registrar evento de ROI
        if (auth.currentUser) {
          let eventType = ROIEventType.IMPROVEMENT;
          if (action === "translate") eventType = ROIEventType.TRANSLATION;
          else if (action === "structure") eventType = ROIEventType.STRUCTURE;
          else if (action === "narrative") eventType = ROIEventType.NARRATIVE;
          else if (action === "optimize") eventType = ROIEventType.OPTIMIZE;
          recordROIEvent(auth.currentUser.uid, eventType);
        }
      }
    } catch (e: any) {
      console.error("Error al aplicar habilidad:", e);
      alert(
        "No se ha podido aplicar la optimización solicitada. Por favor, reintente.",
      );
    } finally {
      setSkillProcessingId(null);
    }
  };

  const renderLayout = () => {
    const config = renderConfigSegment();
    const mic = renderMicSegment();
    const history = renderHistorySegment();
    let ordered =
      micPosition === "top"
        ? [mic, config, history]
        : micPosition === "center"
          ? [config, mic, history]
          : [config, history, mic];
    return (
      <div className="flex flex-col h-full w-full relative" ref={containerRef}>
        <div
          style={{ height: `${panelSizes[0]}%` }}
          className="overflow-hidden min-h-[40px]"
        >
          {ordered[0]}
        </div>
        {renderSeparator((e) => handleResizeStart(0, e))}
        <div
          style={{ height: `${panelSizes[1]}%` }}
          className="overflow-hidden min-h-[40px]"
        >
          {ordered[1]}
        </div>
        {renderSeparator((e) => handleResizeStart(1, e))}
        <div className="flex-1 overflow-hidden min-h-[40px]">{ordered[2]}</div>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col h-full relative overflow-hidden transition-colors ${isDarkMode ? "bg-[#1e2024]" : "bg-white"}`}
    >
      <div
        className={`px-6 py-4 border-b shadow-sm shrink-0 flex items-center justify-between z-20 ${isDarkMode ? "bg-[#2a2d34] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="flex items-center space-x-3">
          <div className="bg-blue-600 p-1.5 rounded-lg shadow-lg shadow-blue-600/20">
            <Mic size={18} className="text-white" />
          </div>
          <h1
            className={`text-xl font-black tracking-tighter uppercase font-['Outfit'] ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
          >
            Transcripción IA
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleDarkMode}
            className={`p-2 rounded-lg border transition-all ${isDarkMode ? "bg-gray-700 border-gray-600 text-yellow-400" : "bg-white border-gray-200 text-gray-400"}`}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </div>
      {renderLayout()}


      <RetryTranscriptionButton
        isDark={isDarkMode}
        moduleName="dictaphone"
        onRetry={async (backup) => {
          setIsProcessing(true);
          try {
            const text = await transcribeAudio(
              backup.blobBase64,
              backup.mimeType,
              (backup.engine as any) || "ia",
            );

            if (text && text.trim().length >= 2) {
              if (isMobileDevice() && auth.currentUser) {
                streamTextToCloud(auth.currentUser.uid, text, 'final');
              }
              const newId = Date.now().toString();
              onSaveTranscription({
                id: newId,
                text,
                mode: "ia",
                createdAt: Date.now(),
              });
              if (auth.currentUser)
                await recordROIEvent(
                  auth.currentUser.uid,
                  ROIEventType.TRANSCRIPTION,
                );
              if (autoCopyRef.current) await performCopy(text, newId);
            }
          } catch (error) {
            alert("El reintento ha fallado. Verifique su conexión.");
            throw error;
          } finally {
            setIsProcessing(false);
          }
        }}
      />
    </div>
  );
};

export default DictaphoneScreen;
